/** Sports league & team color presets — organized by region, grouped by league. */

import { NFL_TEAMS, NBA_TEAMS, NHL_TEAMS, MLB_TEAMS } from './sportsNorthAmericaTeams';
import { sanitizeLightableTuples } from './lightingColorSanitize';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

function rgb(r, g, b) {
	return { r: clamp(r, 0, 255), g: clamp(g, 0, 255), b: clamp(b, 0, 255) };
}

function fitCount(colors, count) {
	const out = [];
	for (let i = 0; i < count; i++) out.push({ ...colors[i % colors.length] });
	return out;
}

function singleLook(tuples, count) {
	const colors = tuples.map(([r, g, b]) => rgb(r, g, b));
	return { type: 'look', colors: fitCount(colors, count) };
}

function cycleLook(lookTuples, count) {
	return {
		type: 'cycle',
		looks: lookTuples.map((tuples) => ({
			colors: fitCount(tuples.map(([r, g, b]) => rgb(r, g, b)), count),
			hold: 30,
		})),
	};
}

function definePreset(id, tuples, cycle) {
	return {
		id,
		generate: (base, n) => (cycle ? cycleLook(tuples, n) : singleLook(tuples, n)),
	};
}

function defineMethod(id, label, description, tuples, cycle = false) {
	return { id, label, description, cycle, tuples: sanitizeLightableTuples(tuples) };
}

function registerPresets(methods, cycle = false) {
	const generators = {};
	methods.forEach((m) => {
		const preset = definePreset(m.id, m.tuples, m.cycle ?? cycle);
		generators[m.id] = preset.generate;
	});
	return { methods: methods.map(({ id, label, description, cycle: c }) => ({ id, label, description, cycle: c })), generators };
}

function teamsToMethods(teams) {
	return teams.map(([id, label, description, tuples]) => defineMethod(id, label, description, tuples));
}

// --- North America — United States ---

const US_LEAGUES = registerPresets([
	defineMethod('sport_nfl', 'NFL (League)', 'Shield red, navy, and white.', [[200, 16, 46], [0, 35, 75], [255, 255, 255], [180, 12, 40]]),
	defineMethod('sport_nba', 'NBA (League)', 'Icon red, white, and blue.', [[200, 16, 46], [29, 66, 138], [255, 255, 255], [180, 20, 50]]),
	defineMethod('sport_nhl', 'NHL (League)', 'Black, silver, and ice white.', [[0, 0, 0], [160, 160, 165], [255, 255, 255], [200, 16, 46]]),
	defineMethod('sport_mlb', 'MLB (League)', 'National pastime red, white, blue.', [[200, 16, 46], [255, 255, 255], [0, 35, 100], [180, 12, 40]]),
	defineMethod('sport_mls', 'MLS (League)', 'League blue, red, and white.', [[0, 80, 160], [200, 16, 46], [255, 255, 255], [0, 60, 120]]),
	defineMethod('sport_wnba', 'WNBA (League)', 'Orange and blue brand colors.', [[255, 100, 0], [0, 50, 120], [255, 255, 255], [200, 80, 0]]),
	defineMethod('sport_ncaa', 'NCAA (Generic)', 'Championship blue and white.', [[0, 51, 102], [255, 255, 255], [200, 16, 46], [0, 40, 85]]),
]);

const US_NFL = registerPresets(teamsToMethods(NFL_TEAMS));
const US_NBA = registerPresets(teamsToMethods(NBA_TEAMS));
const US_NHL = registerPresets(teamsToMethods(NHL_TEAMS));
const US_MLB = registerPresets(teamsToMethods(MLB_TEAMS));

const US_MLS = registerPresets([
	defineMethod('sport_mls_usmnt', 'USMNT', 'USA soccer red, white, blue.', [[200, 16, 46], [255, 255, 255], [0, 35, 100], [180, 12, 40]]),
	defineMethod('sport_mls_lafc', 'LAFC', 'Black and gold.', [[0, 0, 0], [200, 160, 0], [255, 255, 255], [180, 140, 0]]),
	defineMethod('sport_mls_lagalaxy', 'LA Galaxy', 'Navy and gold.', [[28, 45, 92], [255, 209, 0], [255, 255, 255], [20, 35, 75]]),
	defineMethod('sport_mls_inter_miami', 'Inter Miami', 'Pink and black.', [[245, 180, 200], [0, 0, 0], [255, 255, 255], [220, 140, 170]]),
	defineMethod('sport_mls_seattle_sounders', 'Seattle Sounders', 'Green and blue.', [[91, 174, 61], [0, 87, 183], [255, 255, 255], [70, 140, 48]]),
	defineMethod('sport_mls_atlanta_united', 'Atlanta United', 'Red and black.', [[168, 13, 45], [0, 0, 0], [255, 209, 0], [255, 255, 255]]),
]);

// --- North America — Canada & Mexico ---

const NA_CANADA = registerPresets([
	defineMethod('sport_cfl', 'CFL (League)', 'Canadian football red and white.', [[200, 16, 46], [255, 255, 255], [0, 0, 0], [180, 12, 40]]),
	defineMethod('sport_cfl_ticats', 'Hamilton Tiger-Cats', 'Black and gold.', [[0, 0, 0], [255, 197, 47], [255, 255, 255], [200, 160, 10]]),
	defineMethod('sport_cfl_als', 'Alouettes (Montréal)', 'Red, blue, silver.', [[200, 16, 46], [0, 45, 114], [196, 206, 211], [255, 255, 255]]),
	defineMethod('sport_cfl_riders', 'Saskatchewan Roughriders', 'Green and white.', [[0, 120, 60], [255, 255, 255], [200, 16, 46], [0, 90, 45]]),
	defineMethod('sport_canada_soccer', 'Canada National Soccer', 'Red and white maple.', [[200, 16, 46], [255, 255, 255], [0, 0, 0], [180, 12, 40]]),
]);

const NA_MEXICO = registerPresets([
	defineMethod('sport_liga_mx', 'Liga MX (League)', 'Green, white, red.', [[0, 104, 71], [255, 255, 255], [200, 16, 46], [0, 80, 55]]),
	defineMethod('sport_mexico_national', 'Mexico National Team', 'Green, white, red tricolor.', [[0, 104, 71], [255, 255, 255], [200, 16, 46], [0, 80, 55]]),
	defineMethod('sport_club_america', 'Club América', 'Yellow and blue.', [[255, 209, 0], [0, 45, 114], [255, 255, 255], [200, 170, 0]]),
	defineMethod('sport_chivas', 'Chivas Guadalajara', 'Red, white, blue.', [[200, 16, 46], [255, 255, 255], [0, 45, 114], [160, 12, 35]]),
	defineMethod('sport_monterrey', 'Monterrey (Rayados)', 'Royal blue and white.', [[0, 45, 114], [255, 255, 255], [200, 16, 46], [0, 35, 90]]),
]);

// --- South America ---

const SA_BRAZIL = registerPresets([
	defineMethod('sport_brasileirao', 'Brasileirão (League)', 'Green and yellow Brazil tones.', [[0, 120, 60], [255, 220, 0], [255, 255, 255], [0, 90, 45]]),
	defineMethod('sport_brazil_national', 'Brazil National Team', 'Canarinho yellow and green.', [[255, 220, 0], [0, 120, 60], [255, 255, 255], [0, 90, 45]]),
	defineMethod('sport_flamengo', 'Flamengo', 'Red and black.', [[200, 0, 0], [0, 0, 0], [255, 255, 255], [160, 0, 0]]),
	defineMethod('sport_palmeiras', 'Palmeiras', 'Green and white.', [[0, 120, 60], [255, 255, 255], [0, 90, 45], [200, 200, 200]]),
	defineMethod('sport_corinthians', 'Corinthians', 'Black and white.', [[0, 0, 0], [255, 255, 255], [120, 120, 120], [40, 40, 40]]),
]);

const SA_ARGENTINA = registerPresets([
	defineMethod('sport_argentina_national', 'Argentina National Team', 'Sky blue and white stripes.', [[116, 172, 223], [255, 255, 255], [0, 45, 114], [90, 140, 190]]),
	defineMethod('sport_boca_juniors', 'Boca Juniors', 'Blue and gold.', [[0, 45, 114], [255, 209, 0], [255, 255, 255], [0, 35, 90]]),
	defineMethod('sport_river_plate', 'River Plate', 'White and red.', [[200, 16, 46], [255, 255, 255], [0, 0, 0], [160, 12, 35]]),
]);

const SA_OTHER = registerPresets([
	defineMethod('sport_copa_libertadores', 'Copa Libertadores', 'Gold and navy South American club trophy.', [[255, 200, 0], [0, 35, 75], [255, 255, 255], [200, 160, 0]]),
	defineMethod('sport_uruguay_national', 'Uruguay National Team', 'Sky blue and white.', [[135, 206, 235], [255, 255, 255], [0, 0, 0], [100, 170, 210]]),
	defineMethod('sport_colombia_national', 'Colombia National Team', 'Yellow, blue, red.', [[255, 220, 0], [0, 45, 114], [200, 16, 46], [255, 255, 255]]),
]);

// --- Europe ---

const EU_ENGLAND = registerPresets([
	defineMethod('sport_premier_league', 'Premier League', 'Purple and lion green.', [[61, 0, 102], [0, 200, 120], [255, 255, 255], [45, 0, 75]]),
	defineMethod('sport_man_united', 'Manchester United', 'Red and gold.', [[218, 0, 0], [255, 209, 0], [255, 255, 255], [0, 0, 0]]),
	defineMethod('sport_liverpool', 'Liverpool FC', 'Red and white.', [[200, 0, 0], [255, 255, 255], [0, 0, 0], [160, 0, 0]]),
	defineMethod('sport_arsenal', 'Arsenal', 'Red and white.', [[200, 16, 46], [255, 255, 255], [0, 0, 0], [160, 12, 35]]),
	defineMethod('sport_chelsea', 'Chelsea', 'Blue and white.', [[3, 70, 148], [255, 255, 255], [200, 16, 46], [0, 55, 120]]),
	defineMethod('sport_man_city', 'Manchester City', 'Sky blue and white.', [[108, 171, 221], [255, 255, 255], [0, 0, 0], [80, 140, 190]]),
	defineMethod('sport_tottenham', 'Tottenham Hotspur', 'White and navy.', [[255, 255, 255], [19, 34, 87], [0, 0, 0], [200, 200, 200]]),
]);

const EU_SPAIN = registerPresets([
	defineMethod('sport_la_liga', 'La Liga', 'Red and orange brand.', [[200, 16, 46], [255, 130, 0], [255, 255, 255], [0, 0, 0]]),
	defineMethod('sport_real_madrid', 'Real Madrid', 'White and gold.', [[255, 255, 255], [200, 160, 0], [0, 45, 114], [200, 200, 200]]),
	defineMethod('sport_barcelona', 'FC Barcelona', 'Blaugrana stripes.', [[0, 45, 114], [165, 0, 38], [255, 209, 0], [255, 255, 255]]),
	defineMethod('sport_atletico_madrid', 'Atlético Madrid', 'Red and white stripes.', [[200, 16, 46], [255, 255, 255], [0, 45, 114], [160, 12, 35]]),
]);

const EU_GERMANY = registerPresets([
	defineMethod('sport_bundesliga', 'Bundesliga', 'Red and white league.', [[200, 16, 46], [255, 255, 255], [0, 0, 0], [160, 12, 35]]),
	defineMethod('sport_bayern', 'Bayern Munich', 'Red and white.', [[220, 0, 0], [255, 255, 255], [0, 45, 114], [180, 0, 0]]),
	defineMethod('sport_dortmund', 'Borussia Dortmund', 'Yellow and black.', [[255, 209, 0], [0, 0, 0], [255, 255, 255], [200, 170, 0]]),
	defineMethod('sport_germany_national', 'Germany National Team', 'White, black, red.', [[255, 255, 255], [0, 0, 0], [200, 16, 46], [200, 200, 200]]),
]);

const EU_ITALY = registerPresets([
	defineMethod('sport_serie_a', 'Serie A', 'Blue and teal league.', [[0, 45, 114], [0, 130, 130], [255, 255, 255], [0, 35, 90]]),
	defineMethod('sport_juventus', 'Juventus', 'Black and white.', [[0, 0, 0], [255, 255, 255], [120, 120, 120], [40, 40, 40]]),
	defineMethod('sport_ac_milan', 'AC Milan', 'Red and black.', [[200, 16, 46], [0, 0, 0], [255, 255, 255], [160, 12, 35]]),
	defineMethod('sport_inter_milan', 'Inter Milan', 'Blue and black.', [[0, 45, 114], [0, 0, 0], [255, 255, 255], [0, 35, 90]]),
	defineMethod('sport_italy_national', 'Italy National Team', 'Blue and white Azzurri.', [[0, 45, 114], [255, 255, 255], [200, 16, 46], [0, 35, 90]]),
]);

const EU_FRANCE = registerPresets([
	defineMethod('sport_ligue1', 'Ligue 1', 'Blue and white.', [[0, 45, 114], [255, 255, 255], [200, 16, 46], [0, 35, 90]]),
	defineMethod('sport_psg', 'Paris Saint-Germain', 'Navy and red.', [[0, 27, 74], [200, 16, 46], [255, 255, 255], [0, 20, 58]]),
	defineMethod('sport_france_national', 'France National Team', 'Blue, white, red.', [[0, 45, 114], [255, 255, 255], [200, 16, 46], [0, 35, 90]]),
]);

const EU_OTHER = registerPresets([
	defineMethod('sport_uefa_champions', 'UEFA Champions League', 'Starball blue and white.', [[0, 35, 100], [255, 255, 255], [200, 160, 0], [0, 25, 80]]),
	defineMethod('sport_netherlands_national', 'Netherlands National Team', 'Orange and blue.', [[255, 130, 0], [0, 45, 114], [255, 255, 255], [200, 100, 0]]),
	defineMethod('sport_portugal_national', 'Portugal National Team', 'Red and green.', [[200, 16, 46], [0, 120, 60], [255, 255, 255], [160, 12, 35]]),
	defineMethod('sport_celtic', 'Celtic FC (Scotland)', 'Green and white hoops.', [[0, 120, 60], [255, 255, 255], [0, 90, 45], [200, 200, 200]]),
	defineMethod('sport_rangers', 'Rangers FC (Scotland)', 'Royal blue and red.', [[0, 45, 114], [200, 16, 46], [255, 255, 255], [0, 35, 90]]),
]);

// --- Asia & Pacific ---

const APAC_NPB = registerPresets([
	defineMethod('sport_npb', 'NPB (Japanese Baseball)', 'Red and white league.', [[200, 16, 46], [255, 255, 255], [0, 0, 0], [160, 12, 35]]),
	defineMethod('sport_japan_national', 'Japan National Team', 'Samurai blue.', [[0, 45, 114], [255, 255, 255], [200, 16, 46], [0, 35, 90]]),
]);

const APAC_JLEAGUE = registerPresets([
	defineMethod('sport_j_league', 'J-League (Soccer)', 'Red and white league.', [[200, 16, 46], [0, 0, 0], [255, 255, 255], [160, 12, 35]]),
]);

const APAC_AFL = registerPresets([
	defineMethod('sport_afl', 'AFL (Australian Rules)', 'Red and white league.', [[200, 16, 46], [255, 255, 255], [0, 45, 114], [160, 12, 35]]),
]);

const APAC_NRL = registerPresets([
	defineMethod('sport_nrl', 'NRL (Rugby League)', 'Green and gold.', [[0, 120, 60], [255, 220, 0], [255, 255, 255], [0, 90, 45]]),
]);

const APAC_CRICKET = registerPresets([
	defineMethod('sport_cricket_australia', 'Cricket Australia', 'Baggy green and gold.', [[0, 100, 50], [255, 220, 0], [255, 255, 255], [0, 75, 38]]),
]);

const APAC_ALEAGUE = registerPresets([
	defineMethod('sport_a_league', 'A-League (Soccer)', 'Orange and navy.', [[255, 100, 0], [0, 35, 75], [255, 255, 255], [200, 80, 0]]),
]);

const APAC_IPL = registerPresets([
	defineMethod('sport_ipl', 'IPL (Cricket)', 'Blue and gold league.', [[0, 45, 114], [255, 200, 0], [255, 255, 255], [200, 16, 46]]),
]);

const APAC_KLEAGUE = registerPresets([
	defineMethod('sport_k_league', 'K-League (Soccer)', 'Blue and red.', [[0, 45, 114], [200, 16, 46], [255, 255, 255], [0, 35, 90]]),
]);

const APAC_CSL = registerPresets([
	defineMethod('sport_chinese_super_league', 'Chinese Super League', 'Gold and red.', [[255, 200, 0], [200, 16, 46], [255, 255, 255], [0, 45, 114]]),
]);

const APAC_SUPER_RUGBY = registerPresets([
	defineMethod('sport_rugby_nz', 'Super Rugby (All Blacks)', 'Silver and white.', [[0, 0, 0], [180, 180, 180], [255, 255, 255], [40, 40, 40]]),
]);

// --- Africa & Middle East ---

const AF_SUPER_RUGBY = registerPresets([
	defineMethod('sport_springboks', 'Super Rugby (Springboks)', 'Green and gold.', [[0, 120, 60], [255, 220, 0], [255, 255, 255], [0, 90, 45]]),
]);

const AF_PSL = registerPresets([
	defineMethod('sport_premier_soccer_sa', 'PSL (Soccer)', 'Green and gold league.', [[0, 120, 60], [255, 220, 0], [255, 255, 255], [200, 16, 46]]),
]);

const AF_SAUDI = registerPresets([
	defineMethod('sport_saudi_pro_league', 'Saudi Pro League', 'Green and white.', [[0, 120, 60], [255, 255, 255], [200, 16, 46], [0, 90, 45]]),
]);

const AF_UAE = registerPresets([
	defineMethod('sport_uae_soccer', 'UAE Pro League', 'Red, green, and white.', [[200, 16, 46], [0, 120, 60], [255, 255, 255], [0, 0, 0]]),
]);

// --- Global / International ---

const GLOBAL = registerPresets([
	defineMethod('sport_olympics', 'Olympic Games', 'Five-ring blue, yellow, green, and red.', [[0, 129, 203], [255, 217, 0], [0, 159, 76], [200, 16, 46]]),
	defineMethod('sport_fifa_world_cup', 'FIFA World Cup', 'Gold trophy on green pitch.', [[255, 200, 0], [0, 120, 60], [255, 255, 255], [0, 45, 114]]),
	defineMethod('sport_formula1', 'Formula 1', 'Red, black, white racing.', [[200, 16, 46], [0, 0, 0], [255, 255, 255], [200, 200, 200]]),
	defineMethod('sport_ufc', 'UFC', 'Red and black octagon.', [[200, 16, 46], [0, 0, 0], [255, 255, 255], [160, 12, 35]]),
	defineMethod('sport_wwe', 'WWE', 'Red, black, white entertainment.', [[200, 16, 46], [0, 0, 0], [255, 255, 255], [200, 200, 200]]),
	defineMethod('sport_pga', 'PGA Tour (Golf)', 'Navy and gold.', [[0, 35, 75], [255, 200, 0], [255, 255, 255], [0, 25, 58]]),
	defineMethod('sport_tennis_grand_slam', 'Grand Slam Tennis', 'Wimbledon green, US Open blue, clay orange.', [[0, 120, 60], [0, 45, 114], [200, 80, 40], [255, 255, 255]]),
	defineMethod('sport_tour_de_france', 'Tour de France', 'Yellow jersey and French tricolor.', [[255, 220, 0], [0, 45, 114], [255, 255, 255], [200, 16, 46]]),
	defineMethod('sport_rugby_world_cup', 'Rugby World Cup', 'Gold and green.', [[255, 200, 0], [0, 120, 60], [255, 255, 255], [0, 45, 114]]),
	defineMethod('sport_cricket_world_cup', 'Cricket World Cup', 'Blue and gold ICC.', [[0, 45, 114], [255, 200, 0], [255, 255, 255], [200, 16, 46]]),
]);

function mergeGroups(...groupSets) {
	const methods = [];
	const generators = {};
	groupSets.forEach((g) => {
		methods.push(...g.methods);
		Object.assign(generators, g.generators);
	});
	return { methods, generators };
}

const NA_US = mergeGroups(US_LEAGUES, US_NFL, US_NBA, US_NHL, US_MLB, US_MLS);
const NA_ALL = mergeGroups(NA_US, NA_CANADA, NA_MEXICO);
const SA_ALL = mergeGroups(SA_BRAZIL, SA_ARGENTINA, SA_OTHER);
const EU_ALL = mergeGroups(EU_ENGLAND, EU_SPAIN, EU_GERMANY, EU_ITALY, EU_FRANCE, EU_OTHER);
const APAC_ALL = mergeGroups(
	APAC_NPB, APAC_JLEAGUE, APAC_AFL, APAC_NRL, APAC_CRICKET, APAC_ALEAGUE,
	APAC_IPL, APAC_KLEAGUE, APAC_CSL, APAC_SUPER_RUGBY,
);
const AF_ALL = mergeGroups(AF_SUPER_RUGBY, AF_PSL, AF_SAUDI, AF_UAE);

export const SPORTS_GENERATORS = {
	...NA_ALL.generators,
	...SA_ALL.generators,
	...EU_ALL.generators,
	...APAC_ALL.generators,
	...AF_ALL.generators,
	...GLOBAL.generators,
};

export const SPORTS_CATEGORIES = [
	{
		id: 'sports_north_america',
		label: 'Sports — North America',
		groups: [
			{ id: 'na_leagues', label: 'Leagues', methods: US_LEAGUES.methods },
			{ id: 'na_nfl', label: 'NFL', methods: US_NFL.methods },
			{ id: 'na_nba', label: 'NBA', methods: US_NBA.methods },
			{ id: 'na_nhl', label: 'NHL', methods: US_NHL.methods },
			{ id: 'na_mlb', label: 'MLB', methods: US_MLB.methods },
			{ id: 'na_mls', label: 'MLS', methods: US_MLS.methods },
			{ id: 'na_cfl', label: 'CFL', methods: NA_CANADA.methods },
			{ id: 'na_liga_mx', label: 'Liga MX', methods: NA_MEXICO.methods },
		],
	},
	{
		id: 'sports_south_america',
		label: 'Sports — South America',
		groups: [
			{ id: 'sa_brasileirao', label: 'Brasileirão', methods: SA_BRAZIL.methods },
			{ id: 'sa_liga_prof', label: 'Liga Profesional', methods: SA_ARGENTINA.methods },
			{ id: 'sa_libertadores', label: 'Copa Libertadores', methods: SA_OTHER.methods },
		],
	},
	{
		id: 'sports_europe',
		label: 'Sports — Europe',
		groups: [
			{ id: 'eu_premier', label: 'Premier League', methods: EU_ENGLAND.methods },
			{ id: 'eu_laliga', label: 'La Liga', methods: EU_SPAIN.methods },
			{ id: 'eu_bundesliga', label: 'Bundesliga', methods: EU_GERMANY.methods },
			{ id: 'eu_serie_a', label: 'Serie A', methods: EU_ITALY.methods },
			{ id: 'eu_ligue1', label: 'Ligue 1', methods: EU_FRANCE.methods },
			{ id: 'eu_uefa', label: 'UEFA & Continental', methods: EU_OTHER.methods },
		],
	},
	{
		id: 'sports_asia_pacific',
		label: 'Sports — Asia & Pacific',
		groups: [
			{ id: 'apac_npb', label: 'NPB', methods: APAC_NPB.methods },
			{ id: 'apac_jleague', label: 'J-League', methods: APAC_JLEAGUE.methods },
			{ id: 'apac_afl', label: 'AFL', methods: APAC_AFL.methods },
			{ id: 'apac_nrl', label: 'NRL', methods: APAC_NRL.methods },
			{ id: 'apac_cricket', label: 'Cricket Australia', methods: APAC_CRICKET.methods },
			{ id: 'apac_aleague', label: 'A-League', methods: APAC_ALEAGUE.methods },
			{ id: 'apac_ipl', label: 'IPL', methods: APAC_IPL.methods },
			{ id: 'apac_kleague', label: 'K-League', methods: APAC_KLEAGUE.methods },
			{ id: 'apac_csl', label: 'Chinese Super League', methods: APAC_CSL.methods },
			{ id: 'apac_super_rugby', label: 'Super Rugby', methods: APAC_SUPER_RUGBY.methods },
		],
	},
	{
		id: 'sports_africa_me',
		label: 'Sports — Africa & Middle East',
		groups: [
			{ id: 'af_super_rugby', label: 'Super Rugby', methods: AF_SUPER_RUGBY.methods },
			{ id: 'af_psl', label: 'PSL', methods: AF_PSL.methods },
			{ id: 'af_saudi', label: 'Saudi Pro League', methods: AF_SAUDI.methods },
			{ id: 'af_uae', label: 'UAE Pro League', methods: AF_UAE.methods },
		],
	},
	{
		id: 'sports_global',
		label: 'Sports — Global / International',
		groups: [
			{ id: 'global', label: 'International Federations', methods: GLOBAL.methods },
		],
	},
];

/** Flatten all sports methods for lookup. */
export function getSportsMethod(methodId) {
	for (const cat of SPORTS_CATEGORIES) {
		for (const group of cat.groups || []) {
			const method = group.methods.find((m) => m.id === methodId);
			if (method) return { ...method, category: cat.id, region: group.label };
		}
	}
	return null;
}

export function getSportsMethodsFlat(categoryId) {
	const cat = SPORTS_CATEGORIES.find((c) => c.id === categoryId);
	if (!cat) return [];
	if (cat.groups) return cat.groups.flatMap((g) => g.methods);
	return cat.methods || [];
}

export function getCategoryGroups(categoryId) {
	const cat = SPORTS_CATEGORIES.find((c) => c.id === categoryId);
	return cat?.groups || [];
}

export function getCategoryGroup(categoryId, groupId) {
	const cat = SPORTS_CATEGORIES.find((c) => c.id === categoryId);
	return cat?.groups?.find((g) => g.id === groupId) || null;
}

export function findGroupIdForMethod(categoryId, methodId) {
	const cat = SPORTS_CATEGORIES.find((c) => c.id === categoryId);
	for (const group of cat?.groups || []) {
		if (group.methods.some((m) => m.id === methodId)) return group.id;
	}
	return cat?.groups?.[0]?.id || null;
}

export function getFirstSportsMethodId(categoryId, groupId) {
	const group = groupId
		? getCategoryGroup(categoryId, groupId)
		: getCategoryGroups(categoryId)[0];
	return group?.methods?.[0]?.id || null;
}
